import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Anchor,
  Center,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import api from '@/config/api';
import { useAuthStore } from '@/store/auth';

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (v.trim() ? null : 'Email is required'),
      password: (v) => (v ? null : 'Password is required'),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    setError('');
    try {
      // The server sets the JWT as an httpOnly cookie in the response.
      const { data } = await api.post('/auth/login', {
        username: values.email,
        password: values.password,
      });

      const user = data.user;
      login({
        user_id: user.id,
        username: user.username,
        display_name: user.display_name || undefined,
        role: user.role,
        permissions: user.permissions || [],
        customer: null,
      });
      navigate('/dashboard');
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Login failed. Please check your credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center
      mih="100vh"
      style={{
        background: 'linear-gradient(135deg, var(--mantine-color-brand-9) 0%, var(--mantine-color-brand-7) 50%, var(--mantine-color-brand-8) 100%)',
      }}
    >
      <Container size={440} w="100%">
        <Stack align="center" gap={4} mb="xl">
          <img
            src="/logo.png"
            alt="Standard Produce Co."
            style={{
              height: 140,
              filter: 'brightness(0) invert(1)',
              opacity: 0.9,
            }}
          />
          <Text c="rgba(255,255,255,0.7)" size="sm" ta="center">
            Customer Portal
          </Text>
        </Stack>

        <Paper shadow="xl" p={36} radius="lg">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                  {error}
                </Alert>
              )}
              <TextInput
                label="Email"
                placeholder="you@company.com"
                size="md"
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label="Password"
                placeholder="Your password"
                size="md"
                {...form.getInputProps('password')}
              />
              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
                variant="gradient"
                gradient={{ from: 'brand.7', to: 'brand.5', deg: 135 }}
              >
                Sign in
              </Button>
            </Stack>
          </form>
          <Text c="dimmed" size="sm" ta="center" mt="lg">
            Don&apos;t have an account?{' '}
            <Anchor component={Link} to="/register" size="sm">
              Register
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Center>
  );
}
